import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus } from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import NotesSection from "@/components/NotesSection";
import { 
  CountryList, 
  CountryHeader, 
  PackagingSection, 
  EditableEPRCategorySection, 
  ChecklistSection 
} from "@/components/country";

// Predefined waste stream categories
const WASTE_STREAMS = [
  { id: 'packaging', name: 'Packaging EPR', icon: '📦' },
  { id: 'weee', name: 'WEEE', icon: '🔌' },
  { id: 'batteries', name: 'Batteries', icon: '🔋' },
  { id: 'furniture', name: 'Furniture', icon: '🪑' },
  { id: 'textiles', name: 'Textiles', icon: '👕' },
  { id: 'tyres', name: 'Tyres', icon: '🛞' },
];

// Country Detail View Component
function CountryDetail({ countryCode, onBack }) {
  const api = useApi();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddStream, setShowAddStream] = useState(false);
  const [customStreamName, setCustomStreamName] = useState("");
  const [activeStreams, setActiveStreams] = useState([]);

  useEffect(() => {
    fetchCountryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  const fetchCountryData = async () => {
    try {
      // Try to get customized data first
      let countryData;
      try {
        const customRes = await api.get(`/api/country-customizations/${countryCode}`);
        countryData = customRes.data;
      } catch {
        // Fall back to base data
        const countryRes = await api.get(`/api/countries/${countryCode}`);
        countryData = countryRes.data;
      }
      
      setCountry(countryData || null);
      
      // Determine active waste streams
      const streams = [];
      WASTE_STREAMS.forEach(stream => {
        if (countryData?.[stream.id]) {
          streams.push(stream.id);
        }
      });
      // Check for custom streams
      if (countryData?.custom_streams) {
        Object.keys(countryData.custom_streams).forEach(key => {
          if (!WASTE_STREAMS.find(s => s.id === key)) {
            streams.push(key);
          }
        });
      }
      setActiveStreams(streams);
      
    } catch (error) {
      console.error("Error fetching country details:", error);
      setCountry(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomStream = async () => {
    if (!customStreamName.trim()) {
      toast.error("Stream name is required");
      return;
    }
    
    const streamId = customStreamName.toLowerCase().replace(/\s+/g, '_');
    
    try {
      await api.patch(`/api/country-customizations/${countryCode}/field`, {
        field_path: streamId,
        new_value: {
          status: 'planned',
          regulation_name: '',
          effective_date: '',
          major_pros: [],
          scope: [],
          notes: ''
        },
        notes: `Added new waste stream: ${customStreamName}`
      });
      
      toast.success(`${customStreamName} stream added`);
      setShowAddStream(false);
      setCustomStreamName("");
      refreshData();
    } catch (error) {
      console.error("Error adding stream:", error);
      toast.error("Failed to add stream");
    }
  };

  const refreshData = () => {
    setLoading(true);
    fetchCountryData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  if (!country) {
    return (
      <div className="text-center py-8">
        <p className="text-zinc-500 text-sm">Country not found.</p>
        <Button onClick={onBack} variant="outline" className="mt-3">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Countries
        </Button>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="country-detail-view">
      <CountryHeader 
        country={country}
        countryCode={countryCode}
        onBack={onBack}
        onRefresh={refreshData}
      />

      {/* Tabs for different compliance types */}
      <Tabs defaultValue="packaging" className="w-full">
        <TabsList className="flex flex-wrap gap-1 mb-4 h-auto p-1">
          <TabsTrigger value="packaging" className="text-xs" data-testid="tab-packaging">📦 Packaging</TabsTrigger>
          <TabsTrigger value="weee" className="text-xs" data-testid="tab-weee">🔌 WEEE</TabsTrigger>
          <TabsTrigger value="batteries" className="text-xs" data-testid="tab-batteries">🔋 Batteries</TabsTrigger>
          <TabsTrigger value="furniture" className="text-xs" data-testid="tab-furniture">🪑 Furniture</TabsTrigger>
          <TabsTrigger value="textiles" className="text-xs" data-testid="tab-textiles">👕 Textiles</TabsTrigger>
          <TabsTrigger value="tyres" className="text-xs" data-testid="tab-tyres">🛞 Tyres</TabsTrigger>
          <TabsTrigger value="checklist" className="text-xs" data-testid="tab-checklist">📋 Checklist</TabsTrigger>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 text-xs"
            onClick={() => setShowAddStream(true)}
            data-testid="add-stream-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Stream
          </Button>
        </TabsList>

        <TabsContent value="packaging">
          <PackagingSection 
            countryCode={countryCode}
            packaging={country.packaging}
            onRefresh={refreshData}
          />
        </TabsContent>

        <TabsContent value="weee">
          <EditableEPRCategorySection 
            countryCode={countryCode}
            category="weee"
            data={country.weee}
            categoryName="WEEE"
            onRefresh={refreshData}
          />
        </TabsContent>

        <TabsContent value="batteries">
          <EditableEPRCategorySection 
            countryCode={countryCode}
            category="batteries"
            data={country.batteries}
            categoryName="Batteries"
            onRefresh={refreshData}
          />
        </TabsContent>

        <TabsContent value="furniture">
          <EditableEPRCategorySection 
            countryCode={countryCode}
            category="furniture"
            data={country.furniture}
            categoryName="Furniture"
            onRefresh={refreshData}
          />
        </TabsContent>

        <TabsContent value="textiles">
          <EditableEPRCategorySection 
            countryCode={countryCode}
            category="textiles"
            data={country.textiles}
            categoryName="Textiles"
            onRefresh={refreshData}
          />
        </TabsContent>

        <TabsContent value="tyres">
          <EditableEPRCategorySection 
            countryCode={countryCode}
            category="tyres"
            data={country.tyres}
            categoryName="Tyres"
            onRefresh={refreshData}
          />
        </TabsContent>

        <TabsContent value="checklist">
          <ChecklistSection countryCode={countryCode} />
        </TabsContent>
      </Tabs>

      {/* Notes Section */}
      <div className="mt-4">
        <NotesSection 
          entityType="country" 
          entityId={countryCode} 
          entityName={country.country_name}
        />
      </div>

      {/* Add Custom Stream Dialog */}
      <Dialog open={showAddStream} onOpenChange={setShowAddStream}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add New Waste Stream</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Stream Name</Label>
              <Input
                value={customStreamName}
                onChange={(e) => setCustomStreamName(e.target.value)}
                placeholder="e.g., Medical Waste, Oils, etc."
                data-testid="custom-stream-name"
              />
            </div>
            <div className="text-xs text-zinc-500">
              Common streams like Packaging, WEEE, Batteries, Furniture, Textiles, and Tyres are already available in the tabs above. Use this to add custom/country-specific waste streams.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStream(false)}>Cancel</Button>
            <Button onClick={handleAddCustomStream} data-testid="save-custom-stream">
              Add Stream
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Country Compliance Page
export default function CountryCompliance() {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check for country in URL params on mount
  useEffect(() => {
    const countryParam = searchParams.get("country");
    if (countryParam) {
      setSelectedCountry(countryParam.toUpperCase());
    }
  }, [searchParams]);

  const handleSelectCountry = (countryCode) => {
    setSelectedCountry(countryCode);
    setSearchParams({ country: countryCode });
  };

  const handleBack = () => {
    setSelectedCountry(null);
    setSearchParams({});
  };

  return (
    <div className="content-wrapper" data-testid="country-compliance-page">
      <div className="page-header compact">
        <h1 className="page-title">Country Compliance</h1>
        <p className="page-subtitle">
          EPR/PPR requirements for EU27 + UK
        </p>
      </div>

      {selectedCountry ? (
        <CountryDetail countryCode={selectedCountry} onBack={handleBack} />
      ) : (
        <CountryList onSelectCountry={handleSelectCountry} />
      )}
    </div>
  );
}
